import { Check, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
	VerticalReorderingList,
	VerticalReorderingListItem,
} from '@/components/vertical-reordering-list';

import Modal from '@/shared/components/Modal';
import ConfirmDialog from '@/shared/components/confirm-dialog';
import { ShortcutTooltip } from '@/shared/components/shortcut-tooltip/shortcut-tooltip';
import { toast } from '@/shared/components/toast';
import { Tooltip } from '@/shared/components/tooltip';
import { COPY } from '@/shared/constants/copies';
import { CUSTOM_STAGES } from '@/shared/constants/stage';
import { KeyboardKeys, getCommandKeyboardKey } from '@/shared/utils/navigation';

import { cn } from '@/lib/utils';

interface ContextValue {
	editingStageId: string | null;
	editingStageName: string;
	stagesCounts: { stage: string; count: number }[];
	newStageName: string;
	preventDeletion: boolean;
	fixedStages: string[];
	protectedStages: string[];
	setNewStageName: (name: string) => void;
	setEditingStageName: (name: string) => void;
	setEditingStageId: (stageId: string) => void;
	handleAddStage: () => void;
	handleDeleteStage: (stageId: string) => void;
	handleSaveEdit: () => void;
	handleCancelEdit: () => void;
	handleEditStageClick: (stageId: string) => void;
}
const ConfigureStagesContext = createContext<ContextValue>({} as ContextValue);

function useConfigureStages() {
	const context = useContext<ContextValue>(ConfigureStagesContext);
	return context;
}

export interface ConfigureStagesProps extends React.PropsWithChildren {
	stages: { label: string }[];
	fixedStages?: string[];
	protectedStages?: string[];
	stagesCounts?: { stage: string; count: number }[];
	onAdd: (label: string) => void;
	onDelete: (label: string) => void;
	onRename: (oldLabel: string, newLabel: string) => Promise<void>;
	onReorder: (reorderedStages: { label: string }[]) => void;
	suggestedStages?: string[];
	isSuggestionsLoading?: boolean;
	className?: string;
	triggerClassName?: string;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	withoutTrigger?: boolean;
	preventDeletion?: boolean;
	titleContent?: React.ReactNode;
}

const DEFAULT_FIXED_STAGES = [CUSTOM_STAGES.NEW, CUSTOM_STAGES.ARCHIVED];
const DEFAULT_PROTECTED_STAGES = [CUSTOM_STAGES.SHORTLISTED];

function ConfigureStages({
	children,
	stages,
	fixedStages = DEFAULT_FIXED_STAGES,
	protectedStages = DEFAULT_PROTECTED_STAGES,
	stagesCounts = [],
	onAdd,
	onDelete,
	onRename,
	onReorder,
	suggestedStages,
	isSuggestionsLoading,
	className,
	triggerClassName,
	open,
	onOpenChange,
	withoutTrigger = false,
	preventDeletion = false,
	titleContent = 'Manage stages',
}: ConfigureStagesProps) {
	const [newStageName, setNewStageName] = useState('');
	const [editingStageId, setEditingStageId] = useState<string | null>(null);
	const [editingStageName, setEditingStageName] = useState('');
	const [isInputFocused, setIsInputFocused] = useState(false);
	const footerInputRef = useRef<HTMLInputElement>(null);

	const handleInputBlur = useCallback(() => {
		setTimeout(() => setIsInputFocused(false), 150);
	}, []);

	const handleReorderStages = useCallback(
		(reorderedStages: { label: string; id: string }[]) => {
			onReorder(reorderedStages.map((e) => ({ label: e.label })));
		},
		[onReorder]
	);

	const handleAddStage = useCallback(() => {
		if (!newStageName.trim()) return;

		const alreadyExists = stages.some(
			(stage) => stage.label?.toLowerCase() === newStageName.trim().toLowerCase()
		);
		if (alreadyExists) {
			toast({
				title: 'Error',
				description: 'Stage with similar name already exists',
				variant: 'destructive',
			});
			return;
		}
		if (newStageName?.trim()?.length > 30) {
			toast({
				title: 'Error',
				description: 'Stage name must be less than 30 characters',
				variant: 'destructive',
			});
			return;
		}

		onAdd(newStageName.trim());
		setNewStageName('');
		setIsInputFocused(false);
		footerInputRef.current?.blur();
	}, [newStageName, stages, onAdd]);

	const handleDeleteStage = useCallback(
		(stageId: string) => {
			onDelete(stageId);
		},
		[onDelete]
	);

	const handleEditStageClick = useCallback(
		(stageId: string) => {
			const stage = stages.find((s) => s.label === stageId);
			if (stage) {
				setEditingStageId(stageId);
				setEditingStageName(stage.label);
			}
		},
		[stages]
	);

	const handleSaveEdit = useCallback(async () => {
		if (!editingStageId || !editingStageName.trim()) return;
		const alreadyExists = stages.some(
			(stage) => stage.label?.toLowerCase() === editingStageName.trim().toLowerCase()
		);
		if (editingStageId === editingStageName?.trim()) {
			setEditingStageId(null);
			setEditingStageName('');
			return;
		}

		if (alreadyExists) {
			toast({
				title: 'Error',
				description: 'Stage with similar name already exists',
				variant: 'destructive',
			});
			return;
		}

		setEditingStageId(null);
		setEditingStageName('');

		await onRename(editingStageId, editingStageName.trim());
	}, [editingStageId, editingStageName, stages, onRename]);

	const handleCancelEdit = useCallback(() => {
		setEditingStageId(null);
		setEditingStageName('');
	}, []);

	const filteredSuggestions = useMemo(() => {
		if (!suggestedStages?.length) return [];
		const currentLabels = new Set(stages.map((s) => s.label.toLowerCase()));
		const search = newStageName.trim().toLowerCase();
		return suggestedStages
			.filter((s) => !currentLabels.has(s.toLowerCase()))
			.filter((s) => !search || s.toLowerCase().includes(search));
	}, [suggestedStages, stages, newStageName]);

	const showSuggestions = isInputFocused && !isSuggestionsLoading && filteredSuggestions.length > 0;

	return (
		<ConfigureStagesContext.Provider
			value={{
				editingStageId,
				editingStageName,
				stagesCounts,
				newStageName,
				setNewStageName,
				handleAddStage,
				handleDeleteStage,
				handleCancelEdit,
				handleSaveEdit,
				handleEditStageClick,
				setEditingStageName,
				setEditingStageId,
				preventDeletion,
				fixedStages,
				protectedStages,
			}}
		>
			<Modal
				open={open}
				onOpenChange={onOpenChange}
				contentClassName="w-[calc(100vw-2rem)] max-w-md sm:w-full"
				footerClassName="w-full flex-col items-stretch"
				contentProps={{
					onEscapeKeyDown: (e) => {
						const target = e.target as HTMLElement;
						const targetTag = target?.tagName?.toLowerCase?.() || '';
						const isContentEditable = target?.getAttribute('contenteditable') === 'true';
						if (targetTag === 'textarea' || targetTag === 'input' || isContentEditable) {
							e.preventDefault();
							target?.blur();
						}
					},
					onPointerDownOutside: (e) => {
						const target = e.target as HTMLElement;
						if (target?.closest?.('[data-radix-popper-content-wrapper]')) {
							e.preventDefault();
						}
					},
				}}
				triggerContent={
					withoutTrigger
						? null
						: children || (
								<div>
									<Tooltip
										delayDuration={300}
										text="Add or manage stages"
									>
										<Button
											variant={'ghost'}
											size={'icon'}
											className={cn('rounded-md', triggerClassName)}
										>
											<Plus className="size-4" />
										</Button>
									</Tooltip>
								</div>
							)
				}
				titleContent={titleContent}
				footerContent={
					<Popover open={isInputFocused && (isSuggestionsLoading || showSuggestions)}>
						<div className="flex w-full items-center gap-2">
							<PopoverAnchor className="flex-1">
								<Input
									ref={footerInputRef}
									value={newStageName}
									onChange={(e) => e.target.value.length <= 30 && setNewStageName(e.target.value)}
									placeholder="Add another stage..."
									onFocus={() => setIsInputFocused(true)}
									onBlur={handleInputBlur}
								/>
							</PopoverAnchor>
							<Button
								onClick={handleAddStage}
								variant="default"
								size="default"
								type="button"
								disabled={!newStageName?.trim()}
							>
								Add
							</Button>
						</div>

						<PopoverContent
							align="start"
							sideOffset={4}
							onOpenAutoFocus={(e) => e.preventDefault()}
							onCloseAutoFocus={(e) => e.preventDefault()}
							onInteractOutside={(e) => e.preventDefault()}
							className="w-[var(--radix-popover-trigger-width)] rounded-sm p-0"
							style={{ pointerEvents: 'auto' }}
							onWheel={(e) => {
								const viewport = e.currentTarget.querySelector(
									'[data-radix-scroll-area-viewport]'
								) as HTMLElement | null;
								if (!viewport) return;
								const { scrollTop, scrollHeight, clientHeight } = viewport;
								const atTop = scrollTop <= 0 && e.deltaY < 0;
								const atBottom = scrollTop + clientHeight >= scrollHeight && e.deltaY > 0;
								if (!atTop && !atBottom) {
									viewport.scrollTop += e.deltaY;
								}
								e.preventDefault();
								e.stopPropagation();
							}}
						>
							{isSuggestionsLoading && (
								<div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
									<Loader2 className="size-3 animate-spin" />
									Loading suggestions...
								</div>
							)}

							{showSuggestions && (
								<ScrollArea className="max-h-48" viewportClassName="max-h-[calc(12rem-8px)] p-1">
									<div>
										{filteredSuggestions.map((suggestion) => (
											<button
												key={suggestion}
												type="button"
												onMouseDown={(e) => e.preventDefault()}
												onClick={() => {
													setNewStageName(suggestion);
													setIsInputFocused(false);
													footerInputRef.current?.blur();
												}}
												className="flex w-full cursor-pointer select-none items-center rounded-sm px-3 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
											>
												{suggestion}
											</button>
										))}
									</div>
								</ScrollArea>
							)}
						</PopoverContent>
					</Popover>
				}
			>
				<div className={cn('flex flex-col gap-3 pt-2', className)}>
					{stages.some((s) => s.label === fixedStages[0]) && (
						<StageItem
							stage={{
								id: fixedStages[0],
								label: fixedStages[0],
							}}
						/>
					)}

					<VerticalReorderingList
						items={stages
							?.map((e) => ({ ...e, id: e.label }))
							?.filter((e) => !fixedStages.includes(e.id))}
						onReorder={handleReorderStages}
						getItemId={(stage) => stage.id}
						className="flex flex-col gap-3"
						renderItem={(stage) => (
							<StageItem
								key={stage.id}
								stage={stage}
								preventDrag={stages?.length <= 3}
							/>
						)}
					/>

					{stages.some((s) => s.label === fixedStages[fixedStages.length - 1]) && (
						<StageItem
							stage={{
								id: fixedStages[fixedStages.length - 1],
								label: fixedStages[fixedStages.length - 1],
							}}
						/>
					)}
				</div>
			</Modal>
		</ConfigureStagesContext.Provider>
	);
}

export default ConfigureStages;

function StageItem({
	stage,
	className,
	preventDrag = false,
}: {
	className?: string;
	stage: { id: string; label: string };
	preventDrag?: boolean;
}) {
	const {
		stagesCounts,
		editingStageId,
		editingStageName,
		preventDeletion,
		fixedStages,
		protectedStages,
		handleSaveEdit,
		handleCancelEdit,
		handleDeleteStage,
		handleEditStageClick,
		setEditingStageName,
	} = useConfigureStages();

	const isLocked = useMemo(
		() => fixedStages.includes(stage.id) || protectedStages.includes(stage.id),
		[fixedStages, protectedStages, stage.id]
	);

	const allCountsMap = useMemo(() => {
		return stagesCounts.reduce(
			(acc, curr) => {
				acc[curr.stage] = curr.count;
				return acc;
			},
			{} as Record<string, number>
		);
	}, [stagesCounts]);

	return (
		<VerticalReorderingListItem
			id={stage.id}
			key={stage.id}
			className={cn(
				'group flex items-center gap-2 rounded-md border bg-background p-2 shadow-sm',
				editingStageId === stage.id
					? 'h-[46px] [&_input]:!border-none [&_input]:p-0 [&_input]:!shadow-none [&_input]:!outline-none [&_input]:!ring-0'
					: '',
				className
			)}
			moveIconClassName="text-secondary/30 hover:text-secondary"
			hideDragIcon={fixedStages.includes(stage.id) || preventDrag || editingStageId === stage.id}
		>
			{editingStageId === stage.id ? (
				<InputEdit
					value={editingStageName}
					placeholder="Add another stage..."
					setValue={(value) => value?.length <= 30 && setEditingStageName(value)}
					onSave={handleSaveEdit}
					onCancel={handleCancelEdit}
					autoFocusInput
					className="pl-[1px]"
				/>
			) : (
				<p className="flex-1 text-sm font-medium text-primary/70">
					{stage.label}{' '}
					{allCountsMap[stage.id] > 0 && (
						<span className="ml-1.5 font-mono text-sm font-bold text-foreground/60">
							{allCountsMap[stage.id]}
						</span>
					)}
				</p>
			)}

			{editingStageId === stage.id ? null : (
				<div className="flex items-center gap-1">
					<Tooltip
						delayDuration={300}
						content={isLocked ? COPY.fields.stageNotEditable : ''}
					>
						<div>
							<Button
								variant="ghost"
								size="sm"
								disabled={isLocked}
								onClick={() => handleEditStageClick?.(stage.id)}
								className={cn(
									'size-7 p-0 text-muted-foreground/40',
									isLocked ? '' : 'group-hover:text-secondary'
								)}
							>
								<Pencil className="size-3.5" />
							</Button>
						</div>
					</Tooltip>

					{preventDeletion ? null : (
						<Tooltip
							delayDuration={300}
							content={
								isLocked
									? COPY.fields.stageNotDeletable
									: allCountsMap[stage.id]
										? COPY.fields.stageHasCandidates
										: ''
							}
						>
							<div>
								<Button
									variant="ghost"
									size="sm"
									disabled={allCountsMap[stage.id] > 0 || isLocked}
									onClick={() => handleDeleteStage(stage.id)}
									className={cn(
										'size-7 p-0 text-muted-foreground/40',
										isLocked ? '' : 'group-hover:text-secondary'
									)}
								>
									<Trash2 className="size-3.5" />
								</Button>
							</div>
						</Tooltip>
					)}
				</div>
			)}
		</VerticalReorderingListItem>
	);
}

function ConfirmButtons({
	onSave,
	onCancel,
	showCancel = true,
	successDisabled = false,
}: {
	onSave: () => void;
	onCancel: () => void;
	showCancel?: boolean;
	successDisabled?: boolean;
}) {
	const { editingStageName, editingStageId } = useConfigureStages();
	const [confirmOpen, setConfirmOpen] = useState(false);

	return (
		<div className="flex items-center gap-1">
			{!showCancel ? (
				<Button
					onClick={onSave}
					variant="default"
					size="default"
					type="button"
				>
					Add
				</Button>
			) : (
				<ShortcutTooltip
					text="Save"
					keys={[getCommandKeyboardKey(), KeyboardKeys.Enter]}
					hotKeyCallback={() => {
						setConfirmOpen(true);
					}}
					hotKeyDependencies={[onSave]}
					hotKeyOptions={{
						enableOnFormTags: true,
						enabled: !successDisabled,
					}}
				>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => {
							setConfirmOpen(true);
						}}
						className="size-7 p-0 text-secondary"
						disabled={successDisabled}
					>
						<Check className="size-4" />
					</Button>
				</ShortcutTooltip>
			)}

			{showCancel && (
				<ShortcutTooltip
					text="Cancel"
					keys={[KeyboardKeys.Escape]}
					hotKeyCallback={onCancel}
					hotKeyDependencies={[onCancel]}
					hotKeyOptions={{
						enableOnFormTags: true,
					}}
				>
					<Button
						variant="ghost"
						size="sm"
						onClick={onCancel}
						className="size-7 p-0 text-secondary"
					>
						<X className="size-3.5" />
					</Button>
				</ShortcutTooltip>
			)}

			<ConfirmDialog
				isOpen={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				onConfirm={onSave}
				description={COPY.fields.stageRenameConfirm(String(editingStageId), editingStageName)}
				title={COPY.confirms.areYouSure}
				variant="default"
			/>
		</div>
	);
}

function InputEdit({
	value,
	placeholder,
	setValue,
	onSave,
	onCancel,
	autoFocusInput = true,
	showCancel = true,
	className,
}: {
	value: string;
	placeholder: string;
	setValue: (value: string) => void;
	onSave: () => void;
	onCancel: () => void;
	autoFocusInput?: boolean;
	showCancel?: boolean;
	className?: string;
}) {
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (autoFocusInput) inputRef.current?.focus();
	}, [autoFocusInput]);

	return (
		<div className={cn('flex w-full items-center gap-2', className)}>
			<Input
				ref={inputRef}
				value={value}
				onChange={(e) => setValue(e.target.value)}
				className="flex-1"
				placeholder={placeholder}
			/>

			<ConfirmButtons
				onCancel={onCancel}
				onSave={onSave}
				showCancel={showCancel}
				successDisabled={!value?.trim?.()}
			/>
		</div>
	);
}
